import type { JSX, ReactNode } from 'react'

import {
  Dialog as DialogRoot,
  DialogClose,
  DialogContent,
  DialogTitle
} from '../components/ui/dialog.tsx'
import { Button } from './button.tsx'
import { Icon } from './icon.tsx'
import { cn } from '../lib/cn.ts'

/**
 * How much horizontal room a dialog takes, named after `Panel`'s own `width`
 * so the two read the same way at a call site.
 *
 * `form` matches `Panel`'s own `form` measure — the two must agree, because
 * this cap wins over the content (see the component note) and a `form` Panel
 * inside a wider dialog would just leave the extra room empty. `wide` is for
 * a dialog whose content is a table with enough columns that the form measure
 * would force a horizontal scroll a reader has to discover.
 */
const WIDTHS = {
  form: 'sm:max-w-2xl',
  wide: 'sm:max-w-5xl'
} as const satisfies Record<string, string>

export interface DialogProps {
  'data-testid': string
  /**
   * The dialog's accessible name.
   *
   * Required, because Radix announces a dialog with no title as nothing at
   * all and a reader who has just been moved into it cannot tell where they
   * are. It is not drawn — see the component note — so pass the same words
   * the content's own heading uses.
   */
  title: string
  /** Whether the dialog is on screen. The caller owns this state. */
  open: boolean
  /**
   * Called when the dialog asks to close: its own control, Escape, or a click
   * on the overlay. Radix also calls it with `true`, so a caller can pass a
   * setter straight in.
   */
  onOpenChange: (open: boolean) => void
  /**
   * Whether Escape and a click on the overlay may close this dialog.
   *
   * Default true. Pass false while the content holds something a close would
   * destroy — the create form's once-only password is the case this exists
   * for: the endpoint never returns it again, so an accidental Escape loses
   * it for good. The close control stays live either way, because pressing it
   * is deliberate.
   */
  dismissible?: boolean
  /**
   * How much horizontal room the dialog takes; see `WIDTHS`.
   *
   * Defaults to `form`, matching the `Panel` a dialog's content almost always
   * is. Pass `wide` when that content is a table wide enough that `form`
   * would force a horizontal scroll — and set the inner `Panel`'s own `width`
   * to `wide` too, since this cap wins over the content either way (see the
   * component note) and a `form` Panel inside a `wide` dialog just leaves the
   * extra room empty.
   */
  width?: keyof typeof WIDTHS
  /** The dialog's content. A `Panel`, in this console. */
  children: ReactNode
}

/**
 * Dialog — a modal over the console.
 *
 * It contributes exactly three things: the overlay, the focus trap, and one
 * close control. It draws **no frame and no heading of its own**, because the
 * console's unit of content is a `Panel` and a Panel inside the primitive's
 * padded, bordered box is two frames around one thing — the same reason
 * ADR-0004 decision 5 gives for a named `Panel` width over a `className` at
 * the call site. The title is still the dialog's accessible name; only its
 * drawing is dropped.
 *
 * The primitive's own padding, border, fill and shadow are unset from out
 * here rather than edited in `src/components/ui/dialog.tsx`, because the
 * shadcn CLI owns that file (ADR-0004 decision 1). `DataTable` unsets the
 * table container's overflow the same way and for the same reason.
 *
 * The width caps at `props.width` (`form` by default), and that cap WINS over
 * the content: a `width="wide"` Panel inside a `form` dialog is still held to
 * the form measure. So a dialog that holds a table needs its own cap raised
 * to `wide` as well — the close control is positioned against this element,
 * so the box cannot simply be let go.
 *
 * @param props - Dialog props.
 * @param props.title - The dialog's accessible name.
 * @param props.open - Whether the dialog is on screen.
 * @param props.onOpenChange - Called when the dialog asks to close.
 * @param props.dismissible - Whether Escape and the overlay may close it.
 * @param props.width - How much horizontal room to take; see `WIDTHS`.
 * @param props.children - The dialog's content.
 * @returns The rendered dialog.
 */
export function Dialog({
  'data-testid': testId,
  title,
  open,
  onOpenChange,
  dismissible = true,
  width = 'form',
  children
}: DialogProps): JSX.Element {
  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={testId}
        // This wrapper's own control carries a `data-testid`; the primitive's
        // does not, and a hard-coded hook inside a CLI-owned file could not
        // be told apart between two dialogs on one screen anyway.
        showCloseButton={false}
        // Both routes to an ACCIDENTAL close, and both are refused together:
        // a caller that has something to protect has nothing to gain from
        // stopping one and not the other.
        onEscapeKeyDown={event => {
          if (!dismissible) {
            event.preventDefault()
          }
        }}
        onPointerDownOutside={event => {
          if (!dismissible) {
            event.preventDefault()
          }
        }}
        onInteractOutside={event => {
          if (!dismissible) {
            event.preventDefault()
          }
        }}
        className={cn(
          'max-h-dvh overflow-y-auto border-none bg-transparent p-0 shadow-none',
          WIDTHS[width]
        )}
      >
        <DialogTitle data-testid={`${testId}-title`} className="sr-only">
          {title}
        </DialogTitle>
        {children}
        {/*
          LAST in the DOM, though it is drawn at the top right. Radix moves
          focus to the first focusable descendant, and this must not be it:
          when the content holds something a close would destroy, the opening
          stop and the first Tab should reach the content's own controls —
          "Copy password" — not the one control that throws it away.
        */}
        <DialogClose asChild>
          <Button
            data-testid={`${testId}-close`}
            variant="quiet"
            size="icon-sm"
            className="absolute top-4 right-4"
          >
            <Icon name="x" />
            <span className="sr-only">{'Close'}</span>
          </Button>
        </DialogClose>
      </DialogContent>
    </DialogRoot>
  )
}
